import type { RootState } from 'src/redux/store';

import { Box, Button, useTheme, TextField, Typography, InputAdornment } from '@mui/material';

import { useAppSelector } from 'src/hook/hooks';

import { Iconify } from 'src/components/iconify';

import { Spinner, SuccessCheckmark } from './shared-components';
import { ripple, scaleIn, fadeInUp, numberIncrement } from '../animations';
import { CurrencySymbol } from '../../../../pages/setting/components/billing/utils/currency-utils';

type AddFundsFormProps = {
  open: boolean;
  onToggle: () => void;
  amount: string;
  onAmountChange: (value: string) => void;
  onAddFunds: () => void;
  isProcessing: boolean;
  showSuccess: boolean;
  showMoneyEffect: boolean;
  animateAmount: boolean;
  clickedButton: number | null;
  onQuickAmountClick: (amount: number) => void;
  noActivePlan: boolean;
  t: (key: string) => string;
};

export function AddFundsForm({
  open,
  onToggle,
  amount,
  onAmountChange,
  onAddFunds,
  isProcessing,
  showSuccess,
  showMoneyEffect,
  animateAmount,
  clickedButton,
  onQuickAmountClick,
  noActivePlan,
  t,
}: AddFundsFormProps) {
  const theme = useTheme();
  const subscriptionCurrency = useAppSelector((state: RootState) => {
    const billing = (state as any).billing;
    return (
      billing?.activeSubscription?.plan?.currency ||
      billing?.activeSubscription?.plan?.currency_code ||
      'SAR'
    );
  });

  return (
    <Box sx={{ mt: 3 }}>
      {/* Toggle Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          cursor: 'pointer',
          '&:hover': {
            '& > *': {
              color: 'primary.main',
            },
          },
        }}
        onClick={onToggle}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            transition: 'color 0.2s ease-in-out',
          }}
        >
          {t('ADD_FUNDS')}
        </Typography>
        <Iconify
          icon={open ? 'eva:chevron-up-outline' : 'eva:chevron-down-outline'}
          width={18}
          height={18}
          sx={{
            color: 'text.secondary',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>

      {/* Collapsible Content */}
      <Box
        sx={{
          opacity: open ? 1 : 0,
          maxHeight: open ? '250px' : '0px',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: open ? 'translateY(0)' : 'translateY(-10px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mb: 2,
            animation: open ? `${fadeInUp} 0.3s ease-out 0.1s both` : 'none',
          }}
        >
          <Box sx={{ position: 'relative', flex: 1 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder={t('ENTER_AMOUNT')}
              value={amount}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow positive numbers, no negative, no decimals, no commas
                if (value === '' || (/^\d+$/.test(value) && parseFloat(value) >= 0)) {
                  onAmountChange(value);
                }
              }}
              error={
                amount?.includes('.') ||
                amount?.includes(',') ||
                noActivePlan ||
                (amount !== '' && parseFloat(amount) < 0)
              }
              helperText={
                amount?.includes('.') || amount?.includes(',')
                  ? t('DECIMAL_NOT_ALLOWED')
                  : amount !== '' && parseFloat(amount) < 0
                    ? t('NEGATIVE_NOT_ALLOWED')
                    : ''
              }
              disabled={noActivePlan}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CurrencySymbol currency={subscriptionCurrency} sx={{ mr: 0.5 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  animation: animateAmount ? `${numberIncrement} 0.6s ease-in-out` : 'none',
                },
                '& .MuiInputBase-input': {
                  transition: 'all 0.2s ease-in-out',
                  '&::placeholder': {
                    transition: 'all 0.2s ease-in-out',
                  },
                },
              }}
            />
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={onAddFunds}
            disabled={
              !amount ||
              parseFloat(amount) <= 0 ||
              isProcessing ||
              noActivePlan ||
              amount?.includes('.') ||
              amount?.includes(',')
            }
            sx={{
              minWidth: 80,
              borderRadius: 1,
              fontWeight: 600,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              animation: open ? `${scaleIn} 0.3s ease-out 0.15s both` : 'none',
              '&:hover:not(:disabled)': {
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: theme.shadows[6],
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '100%',
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%) scale(0)',
                  animation: `${ripple} 0.6s ease-out`,
                },
              },
              '&:active:not(:disabled)': {
                transform: 'translateY(0) scale(0.98)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              {showSuccess ? (
                <SuccessCheckmark />
              ) : isProcessing ? (
                <>
                  <Spinner size={16} />
                  {showMoneyEffect && (
                    <Typography variant="caption" sx={{ ml: 1 }}>
                      {t('ADDING')}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Iconify
                    icon="mdi:plus-circle-outline"
                    width={16}
                    height={16}
                    sx={{
                      transition: 'transform 0.2s ease-in-out',
                    }}
                  />
                  {t('ADD')}
                </>
              )}
            </Box>
          </Button>
        </Box>
        {noActivePlan && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="error">
              {t('NO_ACTIVE_PLAN')}
            </Typography>
            <Typography variant="caption" color="error">
              {t('NO_ACTIVE_PLAN_DESC')}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            animation: open ? `${fadeInUp} 0.4s ease-out 0.2s both` : 'none',
          }}
        >
          {[50, 100, 200, 500].map((quickAmount, index) => (
            <Box
              key={quickAmount}
              onClick={() => !noActivePlan && onQuickAmountClick(quickAmount)}
              sx={{
                flex: 1,
                p: 1,
                borderRadius: 1,
                cursor: 'pointer',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: open ? `${scaleIn} 0.3s ease-out ${0.1 + index * 0.1}s both` : 'none',
                backgroundColor:
                  clickedButton === quickAmount
                    ? theme.palette.primary.main
                    : theme.palette.mode === 'dark'
                      ? theme.palette.grey[800]
                      : theme.palette.grey[200],
                color:
                  clickedButton === quickAmount
                    ? theme.palette.primary.contrastText
                    : theme.palette.mode === 'dark'
                      ? theme.palette.grey[100]
                      : theme.palette.grey[900],
                border: `1px solid ${
                  clickedButton === quickAmount
                    ? theme.palette.primary.main
                    : theme.palette.mode === 'dark'
                      ? theme.palette.grey[700]
                      : theme.palette.grey[300]
                }`,
                transform: clickedButton === quickAmount ? 'scale(0.95)' : 'scale(1)',
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.dark
                      : theme.palette.primary.lighter,
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.contrastText
                      : theme.palette.primary.main,
                  transform: 'translateY(-3px) scale(1.02)',
                  boxShadow: theme.shadows[4],
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.1)',
                    animation: `${ripple} 0.6s ease-out`,
                  },
                },
                '&:active': {
                  transform: 'translateY(-1px) scale(0.98)',
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {quickAmount}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
